/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  IM_PRICING_GDE_UI_CLAIMIT_DISABLED,
  IM_PRICING_GDE_UI_RESELLERMANAGEMENT_ENABLED,
} from 'src/util/constants';
import {
  StyledText,
  StyledTextSub,
} from '../../../../components/StyledTypography';
import IconButton from '@mui/material/IconButton';
import { useTranslate } from '@im/react-localization';
import { DeatilsResponse } from '../../hooks/deal-details';
import EditStack from './EditStack';
import { useSearchParams } from 'react-router-dom';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import DateEditStack from './DateEditStack';
import AffiliatedEndUserDialog from '../AffiliatedEndUserDialog/AffiliatedEndUserDialog';
import { useCopCountries } from 'src/hooks/copCountries';
import DealDetailsPageClaimItHeader from './DealDetailsPageClaimItHeader';
import Tooltip from '@mui/material/Tooltip';
import { RefreshCWTwo } from '@im/ui-lib/zenith/icons';
import { useGetQuoteNumber } from './DealDetailsPageTable/api';
import useSnackBar from 'src/hooks/useSnackBar';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import ShowBCNListDialog from '../BCNListDialog';
import ShowBCNListDialogNew from '../BCNListDialogNew';

export default function DealDetailsPageSubHeader(
  props: Readonly<{
    cmpEndUserId?: string;
    dealStatus?: string;
    details?: DeatilsResponse;
    edAcopEndUserId?: string;
    edResellerId?: string;
    exception: boolean;
    role: boolean;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    setDatePickerType: React.Dispatch<
      React.SetStateAction<'Effective' | 'Expiration' | null>
    >;
    setOpenDrawer: React.Dispatch<
      React.SetStateAction<false | 'edit-reseller' | 'edit-enduser'>
    >;
  }>
) {
  const { showSnackBar } = useSnackBar();
  const { isCopCountries } = useCopCountries();
  const translate = useTranslate();
  const isResellerManagementEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_RESELLERMANAGEMENT_ENABLED
  );
  const [searchParams] = useSearchParams();
  const formatDateTime = useDateTimeFormatter();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const customerSetId = searchParams.get('customerset_id');
  const businessType = searchParams.get('businessType');
  const [viewAffiliatedEndUSers, setViewAffiliatedEndUSers] =
    useState<string>();
  const [viewEndUSers, setViewEndUSers] = useState<string>();
  const details = props.details;
  const [openBcnList, setOpenBcnList] = useState<boolean>(false);

  const getQuoteNumber = useGetQuoteNumber({
    BidNumber: details?.deal_number ?? '',
    DealId: String(details?.deal_id ?? ''),
    DiscountType: details?.discounttype ?? '',
    ResellerNumber: String(details?.reseller_number ?? ''),
    VendorProgramId: details?.vendor_program_id ?? 0,
  });

  const [quoteNumber, setQuoteNumber] = useState(getQuoteNumber.data);

  const onRefreshQuoteNumberClick = () => {
    getQuoteNumber
      .mutateAsync()
      .then((res) => {
        if (res) {
          setQuoteNumber(res);
          showSnackBar({
            message: translate('VendorProgram.QuoteNumberRefreshed'),
            severity: 'success',
          });
        } else {
          showSnackBar({
            message: translate('VendorProgram.NoQuoteGenerated'),
            severity: 'warning',
          });
        }
      })
      .catch(() => {
        showSnackBar({
          message: translate('VendorProgram.QuoteNotFound'),
          severity: 'error',
        });
      });
  };

  const quoteNumberRefreshIcon = () => (
    <IconButton
      color="primary"
      data-testid="gds-vp-ss-header-refreshQuoteNumber"
      disabled={getQuoteNumber.isLoading}
      onClick={onRefreshQuoteNumberClick}
      size="small"
      sx={{ ml: 1 }}
      title={translate('Common.Refresh')}
    >
      <RefreshCWTwo size={16} />
    </IconButton>
  );
  const isAutoQuoteEnabled = (details: any): boolean => {
    return (
      details?.enable_Custom_OnDemand_Quote === 'Y' ||
      details?.autoQuote === 'Y'
    );
  };
  const showAutoQuote = () => {
    const quoteNumber_ = () => {
      if (getQuoteNumber.isLoading && quoteNumber) return quoteNumber;
      if (getQuoteNumber.isLoading) return `--`;
      return quoteNumber;
    };

    return (
      isAutoQuoteEnabled(details) && (
        <Stack>
          {isCopCountries() ? (
            <Stack>
              <StyledText fontWeight={600} sx={{ textWrap: 'pretty' }}>
                {quoteNumber_() ?? `--`}
              </StyledText>
            </Stack>
          ) : (
            (quoteNumber_() || !details?.autoquotegenerated) && (
              <Stack>
                <StyledText fontWeight={600} sx={{ textWrap: 'pretty' }}>
                  {details?.autoquotegenerated
                    ? quoteNumber_()
                    : translate('VendorProgram.SkipAutoquoteRequested')}
                </StyledText>
              </Stack>
            )
          )}
          <StyledTextSub>
            {translate('VendorProgram.QuoteNumber')}
          </StyledTextSub>
        </Stack>
      )
    );
  };

  const isClaimItDisabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_CLAIMIT_DISABLED
  );

  useEffect(() => {
    if (details) {
      getQuoteNumber.mutateAsync().then((res) => setQuoteNumber(res));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const isResellerEditable = useMemo(() => {
    if (props.exception) {
      if (businessType === 'Subscription') return true;
      if (
        ['ResellerDiscount', 'ResellerEndUserDiscount'].includes(
          details?.discounttype ?? ''
        )
      ) {
        return true;
      }
      return false;
    }
    return false;
  }, [businessType, details?.discounttype, props.exception]);

  const isEndUserEditable = useMemo(() => {
    if (props.exception) {
      if (businessType === 'Subscription') return true;
      if (['ResellerEndUserDiscount'].includes(details?.discounttype ?? '')) {
        return true;
      }
      return false;
    }
    return false;
  }, [businessType, details?.discounttype, props.exception]);

  return (
    <>
      <Stack divider={<Divider />} spacing={1}>
        <Stack direction="row" flexWrap="wrap" mt={2}>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" />}
            spacing={2}
            width="60%"
          >
            <Stack>
              <StyledText fontWeight={600}>{details?.vendor_name}</StyledText>
              <StyledTextSub>{translate('VendorProgram.Vendor')}</StyledTextSub>
            </Stack>
            <Stack>
              <Stack direction="row">
                <StyledText fontWeight={600}>
                  {details?.vendor_program_name}
                </StyledText>
                <IconButton
                  data-testid="gds-vp-ss-header-linkTOvp"
                  onClick={() =>
                    window.open(
                      `/vendor-program/program-detail?vendorId=${
                        details?.vendor_id
                      }&programId=${details?.vendor_program_id}&${
                        businessType === 'Subscription'
                          ? `businessType=${businessType}&`
                          : ''
                      }countryCode=${countryCode}&companyCode=${companyCode}`
                    )
                  }
                  sx={(theme) => ({ padding: `0 ${theme.spacing(0.5)}` })}
                >
                  <LaunchIcon
                    sx={(theme) => ({
                      color: theme.palette.primary[700],
                      fontSize: theme.typography.pxToRem(18),
                    })}
                  />
                </IconButton>
              </Stack>
              <StyledTextSub>
                {translate('VendorProgram.VendorProgram')}
              </StyledTextSub>
            </Stack>
            <Stack>
              <StyledText fontWeight={600}>
                {!details?.sbo_currencycode ? `--` : details?.sbo_currencycode}
              </StyledText>
              <StyledTextSub>{translate('Common.Currency')}</StyledTextSub>
            </Stack>
            {details?.reseller_name && (
              <Stack>
                <Tooltip
                  title={
                    details.reseller_name?.length > 25
                      ? details.reseller_name
                      : ''
                  }
                >
                  <StyledText
                    onClick={() => setOpenBcnList(true)}
                    sx={(theme: any) => ({
                      color: theme.palette.primary[700],
                      cursor: 'pointer',
                      fontWeight: 600,
                      maxWidth: 200,
                      overflow: 'hidden',
                      textDecoration: 'underline',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {details?.reseller_name}
                  </StyledText>
                </Tooltip>
                <StyledTextSub>
                  {translate('VendorProgram.Reseller')}
                </StyledTextSub>
              </Stack>
            )}
            {details?.reseller_number && !props.exception && (
              <Stack>
                <StyledText fontWeight={600}>
                  {details?.reseller_number}
                </StyledText>
                <StyledTextSub>
                  {translate('VendorProgram.ResellerNumber')}
                </StyledTextSub>
              </Stack>
            )}
            {isResellerEditable && (
              <EditStack
                label="Reseller"
                numberText="Reseller"
                numberValue={props.edResellerId}
                onClick={() => props.setOpenDrawer('edit-reseller')}
                role={props.role}
                status={props.dealStatus}
              />
            )}
            <Stack alignItems="center" direction="row">
              {showAutoQuote()}
              {quoteNumberRefreshIcon()}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" />}
            justifyContent="flex-end"
            spacing={2}
            width="40%"
          >
            <Stack>
              <StyledText fontWeight={600}>
                {formatDateTime(details?.deal_ingested_on ?? '', true)}
              </StyledText>
              <Stack direction="row">
                <StyledTextSub>
                  {translate('VendorProgram.UploadDate')}
                </StyledTextSub>
              </Stack>
            </Stack>
            {details?.effective_date && (
              <DateEditStack
                date={details?.effective_date ?? ''}
                name={'Effective'}
                role={props.role}
                setAnchorEl={props.setAnchorEl}
                setDatePickerType={props.setDatePickerType}
                status={props.dealStatus}
                title={translate('VendorProgram.EffectiveDate')}
              />
            )}
            {details?.expiration_date && (
              <DateEditStack
                date={details?.expiration_date ?? ''}
                name={'Expiration'}
                role={props.role}
                setAnchorEl={props.setAnchorEl}
                setDatePickerType={props.setDatePickerType}
                status={props.dealStatus}
                title={translate('VendorProgram.ExpirationDate')}
              />
            )}
            {details?.price_protection_date && (
              <Stack>
                <StyledText fontWeight={600}>
                  {formatDateTime(details?.price_protection_date ?? '')}
                </StyledText>
                <StyledTextSub>
                  {translate('VendorProgram.PriceProtectionDate')}
                </StyledTextSub>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" flexWrap="wrap">
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" />}
            spacing={2}
          >
            {details?.end_user_name && (
              <Stack
                direction="row"
                divider={
                  <Divider orientation="vertical" sx={{ height: '50%' }} />
                }
                spacing={2}
              >
                <Stack>
                  <Tooltip
                    title={
                      details?.end_user_name?.length > 25
                        ? details.end_user_name
                        : ''
                    }
                  >
                    <StyledText
                      fontWeight={600}
                      sx={{
                        cursor:
                          details?.end_user_name?.length > 25
                            ? 'pointer'
                            : 'default',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {details?.end_user_name}
                    </StyledText>
                  </Tooltip>
                  <StyledTextSub>
                    {translate('VendorProgram.EndUser')}
                  </StyledTextSub>
                </Stack>
                {details?.end_user_address1 && (
                  <Stack>
                    <Tooltip
                      title={
                        details.end_user_address1.length > 18
                          ? details.end_user_address1
                          : ''
                      }
                    >
                      <StyledText
                        sx={{
                          cursor:
                            details.end_user_address1.length > 18
                              ? 'pointer'
                              : 'default',
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {details.end_user_address1}
                      </StyledText>
                    </Tooltip>
                  </Stack>
                )}
                {details?.end_user_address2 && (
                  <Stack>
                    <Tooltip
                      title={
                        details.end_user_address2.length > 18
                          ? details.end_user_address2
                          : ''
                      }
                    >
                      <StyledText
                        sx={{
                          cursor:
                            details.end_user_address2.length > 18
                              ? 'pointer'
                              : 'default',
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {details.end_user_address2}
                      </StyledText>
                    </Tooltip>
                  </Stack>
                )}
                {(details?.end_user_city || details?.end_user_state) && (
                  <Stack>
                    <StyledText>{`${details.end_user_city}${
                      details.end_user_city && details.end_user_state && ', '
                    }${details.end_user_state}`}</StyledText>
                  </Stack>
                )}
                {details?.end_user_zipcode && (
                  <Stack>
                    <StyledText>{details.end_user_zipcode}</StyledText>
                  </Stack>
                )}
                {details?.end_user_country && (
                  <Stack>
                    <StyledText>{details.end_user_country}</StyledText>
                  </Stack>
                )}
              </Stack>
            )}
            {details?.end_user_number && !props.exception && (
              <Stack>
                <StyledText fontWeight={600}>
                  {details?.end_user_number}
                </StyledText>
                <StyledTextSub>
                  {translate('VendorProgram.EnduserNumber')}
                </StyledTextSub>
              </Stack>
            )}
            {isEndUserEditable && (
              <EditStack
                label="Enduser"
                numberText={
                  businessType === 'Subscription' ? 'CmpEnduser' : 'Enduser'
                }
                numberValue={props.edAcopEndUserId}
                onClick={() => props.setOpenDrawer('edit-enduser')}
                role={props.role}
                status={props.dealStatus}
              />
            )}
            {details?.cmp_end_user_id &&
              !props.exception &&
              (businessType === 'Subscription' ||
                details?.businessType === 'Hybrid') && (
                <Stack>
                  <StyledText fontWeight={600}>
                    {details?.cmp_end_user_id}
                  </StyledText>
                  <StyledTextSub>
                    {translate('VendorProgram.CmpEnduserIdNumber')}
                  </StyledTextSub>
                </Stack>
              )}
            {props.exception &&
              details?.discounttype !== 'ResellerDiscount' &&
              (businessType === 'Subscription' ||
                details?.businessType === 'Hybrid') && (
                <Stack direction="column">
                  <StyledText fontWeight={600}>
                    {props.cmpEndUserId && props.cmpEndUserId !== 'null'
                      ? props.cmpEndUserId
                      : '\u00A0'}
                    {/* Added u00A0 for maintaining space in layout */}
                  </StyledText>
                  <StyledTextSub>
                    {translate('VendorProgram.CmpEnduserIdNumber')}
                  </StyledTextSub>
                </Stack>
              )}
            <Stack>
              {details?.euaffiliationcount !== '0' &&
                details?.euaffiliationcount !== null &&
                details?.discounttype !== 'ResellerDiscount' && (
                  <Stack>
                    <StyledText
                      fontWeight={600}
                      onClick={() =>
                        setViewAffiliatedEndUSers(String(details?.deal_id))
                      }
                      variant="link"
                    >
                      {translate('VendorProgram.AffiliatedEndUsersCount', {
                        count: details?.euaffiliationcount,
                      })}
                    </StyledText>

                    <StyledTextSub>
                      {translate('VendorProgram.EndUserAffiliates')}
                    </StyledTextSub>
                  </Stack>
                )}
            </Stack>
          </Stack>
          <Stack
            style={{
              alignSelf: 'flex-end',
              marginLeft: 'auto',
            }}
          >
            {!isClaimItDisabled && (
              <DealDetailsPageClaimItHeader details={details} />
            )}
          </Stack>
        </Stack>
      </Stack>
      {viewAffiliatedEndUSers && (
        <AffiliatedEndUserDialog
          enduser={viewAffiliatedEndUSers}
          handleClose={() => setViewAffiliatedEndUSers(undefined)}
          isAffiliated={true}
        />
      )}
      {viewEndUSers && (
        <AffiliatedEndUserDialog
          enduser={viewEndUSers}
          handleClose={() => setViewEndUSers(undefined)}
          isAffiliated={false}
        />
      )}

      {openBcnList &&
        (isResellerManagementEnabled ? (
          <ShowBCNListDialogNew
            BcnCameOnDeal={details?.BcnCameOnDeal ?? false}
            bcn_linking_level={String(details?.bcn_Linking_Level)}
            coG_Approval={String(details?.coG_Approval ?? '')}
            customerSetId={String(customerSetId)}
            dealId={String(details?.deal_id)}
            handleClose={() => setOpenBcnList(false)}
            isResolvedOnException={false}
            resellerAuth={String(details?.reseller_Authorization)}
            resellerName={String(details?.reseller_name)}
            vendor_program_id={details?.vendor_program_id}
          />
        ) : (
          <ShowBCNListDialog
            BcnCameOnDeal={details?.BcnCameOnDeal ?? false}
            bcn_linking_level={String(details?.bcn_Linking_Level)}
            customerSetId={String(customerSetId)}
            dealId={String(details?.deal_id)}
            handleClose={() => setOpenBcnList(false)}
            isResolvedOnException={false}
            resellerAuth={String(details?.reseller_Authorization)}
            resellerName={String(details?.reseller_name)}
            vendor_program_id={details?.vendor_program_id}
          />
        ))}
    </>
  );
}
